import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { nameGlobMatches, useDatasetNames } from '@/features/catalog-browser';
import { severityTone, useBindings, useRules } from '@/features/rules';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import type { ImpactAnswer, Node } from '../api/lineage';

/* What depends on this node, connecting lineage to data quality.
 *
 * **Why this lives beside the graph and not on a separate screen.** The graph answers "what is this
 * connected to"; this answers "what would notice if it broke" — a rule bound to the dataset, and
 * everything downstream that the graph's own `impact` answer already reaches. Both questions are
 * about the one node the reader is looking at, so a second navigation to answer the second question
 * would cost a context switch for information already one request away.
 *
 * **Collapsed by default, for the same reason `LineageScreen`'s own history argues against a
 * permanent panel.** That screen used to carry six panels and was cut back to "the graph, and
 * nothing else" because every extra panel restated in words what the picture already shows or spent
 * a request nobody asked for. This one is neither: a bound rule and a downstream dataset are not in
 * the picture at all — the graph draws *nodes*, not the rules that watch them — and it spends no new
 * request, since `impact` is what `NodeLineage` already fetched to draw the graph's right half. So it
 * earns a place, and it earns it collapsed: the headline sentence is the answer for a reader who is
 * just confirming nothing depends on this table, and the tables are one click away for the reader who
 * needs to act on it.
 *
 * **Keyed on `dataset_id`, not on `kind === 'dataset'`.** A `column` node's `dataset_id` is the
 * dataset it belongs to, and a rule bound to that dataset is exactly as relevant when the reader
 * opened lineage from one of its columns. Only a `job` node has no dataset behind it, and for a job
 * this renders nothing — there is no rule a job can be bound to and no `impact` answer computed for
 * one from this screen today.
 *
 * **No "risk" score, no dashboards, no pipelines.** This platform's lineage graph has exactly three
 * node kinds — `dataset`, `column`, `job` — and rule severity is the one real signal this screen has
 * for how much a dependency matters. Inventing a risk figure or a BI-asset kind the API does not
 * report would be exactly the fabricated metric CLAUDE.md's own review culture exists to catch; the
 * severity badge on each bound rule is the honest substitute.
 */

export function DqImpactCard({
  subject,
  impact,
  upstreamCount,
}: {
  readonly subject: Node;
  /** `undefined` while the impact read is still in flight. */
  readonly impact: ImpactAnswer | undefined;
  /** How many nodes provenance already reached, for the one-line note below the headline. */
  readonly upstreamCount: number | undefined;
}): ReactElement | null {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const datasetId = subject.dataset_id;
  const names = useDatasetNames();
  const bindings = useBindings();
  const rules = useRules('');

  /* No dataset behind this node — a `job` — so there is nothing here to bind a rule to and nothing
     for this card to say. Called after every hook above so the hook order never depends on which
     kind of node the reader is looking at. */
  if (datasetId === null || datasetId === undefined) return null;

  const qualified = names.data?.byId.get(datasetId) ?? null;
  const rulesById = new Map(
    (rules.data?.items ?? []).map((rule) => [rule.id, rule] as const),
  );
  const matched = (bindings.data?.items ?? []).filter((binding) =>
    binding.selectors.some(
      (selector) =>
        selector.kind === 'name_glob' &&
        qualified !== null &&
        typeof selector.pattern === 'string' &&
        nameGlobMatches(selector.pattern, qualified),
    ),
  );
  const downstream = impact?.affected ?? [];
  const loadingRules = bindings.isPending || rules.isPending || names.isPending;

  return (
    <div className="lq-dqimpact" style={{ marginTop: 'var(--space-5)' }}>
      <div className="lq-band lq-band--info">
        <div className="lq-band__title">
          {t('lineage.dqImpact.banner', {
            rules: loadingRules ? 0 : matched.length,
            downstream: impact === undefined ? 0 : downstream.length,
          })}
        </div>
        {upstreamCount !== undefined && (
          <div className="lq-band__body">
            {t('lineage.dqImpact.upstreamNote', { upstream: upstreamCount })}
          </div>
        )}
      </div>

      <Button
        size="compact"
        onClick={() => {
          setExpanded((value) => !value);
        }}
        aria-expanded={expanded}
      >
        {expanded ? t('lineage.dqImpact.hide') : t('lineage.dqImpact.view')}
      </Button>

      {expanded && (
        <div className="lq-dqimpact__body" style={{ marginTop: 'var(--space-4)' }}>
          <h3 className="lq-subhead">{t('lineage.dqImpact.boundRules')}</h3>
          {loadingRules ? (
            <SkeletonRows rows={2} columns={3} />
          ) : matched.length === 0 ? (
            <p className="lq-muted" style={{ margin: 0 }}>
              {t('lineage.dqImpact.noRules')}
            </p>
          ) : (
            <DataTable
              caption={t('lineage.dqImpact.rulesCaption')}
              columns={[
                { key: 'rule', label: t('lineage.dqImpact.column.rule') },
                { key: 'kind', label: t('lineage.dqImpact.column.kind') },
                { key: 'severity', label: t('lineage.dqImpact.column.severity') },
              ]}
            >
              {matched.map((binding) => {
                const rule = rulesById.get(binding.rule_id);
                return (
                  <tr key={binding.id}>
                    <td>
                      {rule === undefined ? (
                        <span className="lq-dim">
                          {t('lineage.dqImpact.unresolvedRule')}
                        </span>
                      ) : (
                        <Link to={`/rules/${encodeURIComponent(rule.id)}`}>
                          {rule.name}
                        </Link>
                      )}
                    </td>
                    <td className="lq-muted">
                      {rule === undefined ? '—' : t(`rules.kind.${rule.kind}`)}
                    </td>
                    <td>
                      <StatusBadge
                        tone={severityTone(binding.severity)}
                        label={t(`rules.severity.${binding.severity}.label`)}
                        title={t(`rules.severity.${binding.severity}.meaning`)}
                      />
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          )}

          <h3 className="lq-subhead" style={{ marginTop: 'var(--space-5)' }}>
            {t('lineage.dqImpact.downstream')}
          </h3>
          {impact === undefined ? (
            <SkeletonRows rows={2} columns={3} />
          ) : downstream.length === 0 ? (
            <p className="lq-muted" style={{ margin: 0 }}>
              {t('lineage.dqImpact.noDownstream')}
            </p>
          ) : (
            <DataTable
              caption={t('lineage.dqImpact.downstreamCaption')}
              columns={[
                { key: 'asset', label: t('lineage.dqImpact.column.asset') },
                { key: 'kind', label: t('lineage.dqImpact.column.kind') },
                { key: 'hops', label: t('lineage.dqImpact.column.hops'), numeric: true },
              ]}
            >
              {downstream.map((node) => (
                <tr key={node.node_id}>
                  <td>{node.display_name}</td>
                  <td className="lq-muted">{t(`lineage.kind.${node.kind}`)}</td>
                  <td style={{ textAlign: 'right' }}>{node.depth}</td>
                </tr>
              ))}
              {!impact.complete && (
                <tr>
                  <td colSpan={3} className="lq-dim">
                    {t('lineage.dqImpact.incomplete')}
                  </td>
                </tr>
              )}
            </DataTable>
          )}
        </div>
      )}
    </div>
  );
}
